/**
 * Shared pieces of the Access screen (ADR-034): its sections, the error text,
 * and the labels — role and permission labels are the APP's text, shown
 * attributed to it, never presented as Fias's.
 */
import { ClipboardCheck, Inbox, Settings, UserRound, Users, type LucideIcon } from 'lucide-react';
import type { AppAccessPermissionDto, VaultHolderMemberDto, WorkspaceAppRoleDto } from '@/lib/api';
import { StepUpFetchError } from '@/lib/step-up-fetch';

export type AppAccessSectionId = 'people' | 'requests' | 'reviews' | 'settings' | 'my-access';

export interface AppAccessSection {
  id: AppAccessSectionId;
  label: string;
  icon: LucideIcon;
  /** Shown to access managers and reviewers only; "My access" is everyone's. */
  governance: boolean;
}

export const APP_ACCESS_SECTIONS: readonly AppAccessSection[] = [
  { id: 'people', label: 'People and roles', icon: Users, governance: true },
  { id: 'requests', label: 'Requests', icon: Inbox, governance: true },
  { id: 'reviews', label: 'Reviews and export', icon: ClipboardCheck, governance: true },
  { id: 'settings', label: 'Settings', icon: Settings, governance: true },
  { id: 'my-access', label: 'My access', icon: UserRound, governance: false },
];

export function isAppAccessSectionId(value: string | undefined): value is AppAccessSectionId {
  return APP_ACCESS_SECTIONS.some((s) => s.id === value);
}

/** The URL of one section — the same one the server's notices link to. */
export function appAccessSectionPath(
  holderId: string,
  archeId: string,
  section: AppAccessSectionId,
): string {
  return `/workspaces/${encodeURIComponent(holderId)}/apps/${encodeURIComponent(archeId)}/access/${section}`;
}

const ERROR_TEXT: Record<string, string> = {
  MFA_REQUIRED_FOR_COMPANY:
    'Managing who can use an app’s data requires two-factor authentication. Enable it in Account Settings, then try again.',
  HUMAN_SESSION_REQUIRED: 'This change can only be made signed in on Fias in your browser.',
  STEP_UP_REQUIRED: 'Confirm it’s you to make this change.',
  ACCESS_MANAGER_REQUIRED: 'Only an access manager for this app can do that.',
  ALREADY_APPOINTED: 'They already hold that appointment.',
  ALREADY_ASSIGNED: 'They already hold that role here.',
  ALREADY_PROPOSED: 'That role is already proposed for them, waiting for a second person.',
  ALREADY_REQUESTED: 'You already have an open request for this app.',
  APP_HAS_NO_ROLES: 'This app declares no roles.',
  ASSIGNEE_NOT_A_MEMBER: 'They are not an active member of this workspace.',
  INVALID_EXPIRY: 'Choose an expiry in the future.',
  PROPOSAL_NOT_FOUND: 'That proposal is no longer open.',
  REQUEST_LIMIT_REACHED: 'You have asked for access too many times today. Try again tomorrow.',
  REVIEW_IN_PROGRESS: 'A review of this app is already open.',
  REVIEW_ITEM_NOT_FOUND: 'That review item is no longer open.',
  ROLES_ALREADY_ACTIVE: 'Roles are already turned on for this app here.',
  ROLES_NOT_ACTIVE: 'Roles are not turned on for this app here yet.',
  ROLE_NOT_SENSITIVE: 'That role is not sensitive; grant it directly.',
  SECOND_OWNER_REQUIRED:
    'This workspace wants a second person for owners’ sensitive changes, so one owner cannot accept this role’s new permissions alone. To accept them, an owner first turns off “Owners also need a second person” in Settings — the other owners are told.',
  SECOND_PERSON_REQUIRED:
    'A second access manager has to approve this — not the person who proposed it, and not the person receiving it.',
  SEED_REQUIRED: 'Give at least one person a role to turn roles on.',
  SENSITIVE_ROLE_REQUIRES_APPROVAL:
    'This role is sensitive: propose it, and a second access manager approves it.',
  NOT_FOUND: 'Not found — it may have changed since this page loaded.',
  // Audiences (ADR-034 phase 2).
  AUDIENCE_NOT_FOUND: 'That audience is not one this app’s records can be placed in.',
  AUDIENCE_NOT_RETIRED: 'Only a retired audience’s records can be shown to another audience.',
  ROLES_NOT_ACTIVATING: 'Roles are already fully on for this app here.',
  // Same moment, reached by another route: answered alike.
  ROLES_ALREADY_ON:
    'Roles are already fully on for this app here; records still waiting are shown below.',
  PLACEMENT_INVALID: 'That collection cannot be placed there — reload and choose again.',
  // A placement or re-disclosure batch met a lock or a time limit: nothing of
  // it was kept, so trying again is safe (placing retries a couple of times
  // by itself first).
  PLACEMENT_BUSY:
    'Placing the records was held up by other work in this workspace, and nothing from that try was kept. Try again in a moment.',
  PLACEMENT_IN_PROGRESS:
    'Many records are still being placed. What is placed stays placed — select “Place records and finish” to place the rest.',
  CONSTRAINT_VIOLATION:
    'Some of these records point at records in another collection: choose the same audience for both.',
  // Retried once already (useAppAccess.ts retryOnceIfBusy) before this shows.
  ACCESS_BUSY: 'Access is being changed by someone else; try again.',
  WORKSPACE_REQUEST_LIMIT_REACHED:
    'You’ve asked for access to too many apps in this workspace today. Try again tomorrow.',
  // A publish changed the app's roles between the screen loading and the write.
  ACCESS_POLICY_CHANGED:
    'This app’s roles changed since this page loaded, so nothing was changed. The page now shows them as they are — review them, then try again.',
};

/** An audience's label — the app's text — or its id. */
export function audienceLabel(
  audiences: readonly { id: string; label: string }[],
  audienceId: string,
): string {
  return audiences.find((a) => a.id === audienceId)?.label ?? audienceId;
}

/** The specific code an error carries (the Access routes answer with it). */
export function appAccessErrorCode(err: unknown): string | null {
  if (err instanceof StepUpFetchError) return err.code ?? null;
  const code = (err as { code?: unknown })?.code;
  if (typeof code === 'string') return code;
  const message = err instanceof Error ? err.message : '';
  return /^([A-Z][A-Z0-9_]+):/.exec(message)?.[1] ?? null;
}

export function describeAppAccessError(err: unknown, fallback = 'Please try again.'): string {
  const code = appAccessErrorCode(err);
  if (code && code in ERROR_TEXT) return ERROR_TEXT[code];
  return err instanceof Error && err.message ? err.message : fallback;
}

/** A member's name, as Workspace Controls shows it. */
export function memberLabel(member: VaultHolderMemberDto | undefined, principalId: string): string {
  if (member?.displayName) return member.displayName;
  if (member?.username) return `@${member.username}`;
  return `someone (…${principalId.slice(-6)})`;
}

export function roleLabel(roles: readonly WorkspaceAppRoleDto[], roleId: string): string {
  return roles.find((r) => r.roleId === roleId)?.label ?? roleId;
}

export function permissionLabel(
  permissions: readonly AppAccessPermissionDto[],
  permissionId: string,
): string {
  return permissions.find((p) => p.id === permissionId)?.label ?? permissionId;
}

const TIER_RANK = { viewer: 0, member: 1, admin: 2, owner: 3 } as const;

/** Whether a workspace tier meets a role's minimum (the server re-checks on every request). */
export function tierMeets(tier: keyof typeof TIER_RANK, minTier: keyof typeof TIER_RANK): boolean {
  return TIER_RANK[tier] >= TIER_RANK[minTier];
}

export function formatDate(iso: string | null): string {
  return iso ? new Date(iso).toLocaleDateString() : '';
}
